import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Job } from '../types/entities';
import { RootState } from './store';
import { baseUrlApi } from '../../utils/url';

interface JobState {
  jobs: Job[];
  job: Job | null;
  loading: boolean;
  error: string | null;
}

const initialState: JobState = {
  jobs: [],
  job: null,
  loading: false,
  error: null,
};

export const fetchJobs = createAsyncThunk<Job[], void, { state: RootState }>(
  'job/fetchJobs',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/job`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Échec du chargement des jobs');
    }
  }
);

export const fetchJob = createAsyncThunk<Job, number, { state: RootState }>(
  'job/fetchJob',
  async (jobId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.get(`${baseUrlApi}/job/${jobId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchJobs());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Échec du chargement du job');
    }
  }
);

export const createJob = createAsyncThunk<Job, Partial<Job>, { state: RootState }>(
  'job/createJob',
  async (jobData, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.post(`${baseUrlApi}/job`, jobData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchJobs());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Échec de la création du job');
    }
  }
);

export const updateJob = createAsyncThunk<
  Job,
  { jobData: Partial<Job>; jobId: number },
  { state: RootState }
>(
  'job/updateJob',
  async ({ jobData, jobId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      const response = await axios.put(`${baseUrlApi}/job/${jobId}`, jobData, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchJobs());
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Échec de la mise à jour du job');
    }
  }
);

export const deleteJob = createAsyncThunk<number, number, { state: RootState }>(
  'job/deleteJob',
  async (jobId, { getState, dispatch, rejectWithValue }) => {
    try {
      const { authentification } = getState();
      await axios.delete(`${baseUrlApi}/job/${jobId}`, {
        headers: { Authorization: `bearer ${authentification.user.token}` }
      });
      dispatch(fetchJobs());
      return jobId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Échec de la suppression du job');
    }
  }
);

const jobSlice = createSlice({
  name: 'job',
  initialState,
  reducers: {
    clearJob: (state) => { state.job = null; },
    clearJobs: (state) => { state.jobs = []; },
    resetJobState: () => initialState,
  },
  extraReducers: (builder) => {
    [fetchJobs, fetchJob, createJob, updateJob, deleteJob].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
        });
    });

    builder
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.jobs = action.payload;
      })
      .addCase(fetchJob.fulfilled, (state, action) => {
        state.loading = false;
        state.job = action.payload;
      })
      .addCase(createJob.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateJob.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.loading = false;
        state.jobs = state.jobs.filter(item => item.id !== action.payload);
      });
  },
});

export const { clearJob, clearJobs, resetJobState } = jobSlice.actions;
export default jobSlice.reducer;